import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import ImagePickerDialog from "@/components/media/ImagePickerDialog";
import { PanelHeader } from "./PagesPanel";
import { useEditor } from "./useEditorState";
import { assetUrl } from "@/lib/api";

const URL_RE = /https?:\/\/[^\s"'`)\\<>]+/g;
const ASSET_RE = /\/api\/public\/assets\/[A-Za-z0-9_-]+/g;
const BRAND_RE = /data:image\/svg\+xml,%3Csvg[^"'`)\\<>]+/g;
const isImage = (u) => /(images\.unsplash\.com|plus\.unsplash\.com|images\.pexels\.com)/i.test(u) || /\.(png|jpe?g|webp|gif|avif|svg)(\?|$)/i.test(u);
const ORIGIN = { stock: "Stock", upload: "Caricata", ai: "AI", logo: "Logo", external: "Esterna", brand: "Grafica brand" };

export function useSiteImages(content, assets) {
  return useMemo(() => {
    const raw = JSON.stringify(content || {});
    const counts = new Map();
    for (const u of raw.match(URL_RE) || []) { const url = u.replace(/[.,;]+$/, ""); if (isImage(url)) counts.set(url, (counts.get(url) || 0) + 1); }
    for (const u of raw.match(ASSET_RE) || []) counts.set(u, (counts.get(u) || 0) + 1);
    for (const u of raw.match(BRAND_RE) || []) counts.set(u, (counts.get(u) || 0) + 1);
    return [...counts.entries()].map(([url, count]) => {
      const a = url.startsWith("/api/") ? assets.find((x) => x.url === url) : null;
      if (url.startsWith("/api/") && a && !a.content_type?.startsWith("image/")) return null;
      const origin = a ? (a.kind === "logo" ? "logo" : a.folder === "AI" || /^ai_/.test(a.origin || "") ? "ai" : "upload") : url.startsWith("data:") ? "brand" : /unsplash|pexels/i.test(url) ? "stock" : "external";
      return { url, count, origin, name: a?.name };
    }).filter(Boolean);
  }, [content, assets]);
}

function PhotoSlots({ ed }) {
  const { api, id, saveState, save, replaceContent, content } = ed;
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (saveState !== "saved") return; // after an undo, ask only once the undone content is stored
    api.get(`/projects/${id}/images/slots`).then(({ data }) => setSt(data)).catch(() => setSt(null));
  }, [api, id, content, saveState]);
  if (!st || (!st.pending.length && !st.placed.length)) return null;
  const ready = st.placed.length + st.pending.filter((x) => x.photo_ready && x.has_prop).length;
  const insert = async () => {
    setBusy(true);
    try {
      if (saveState === "dirty") await save();
      const { data } = await api.post(`/projects/${id}/images/place`);
      if (data.content) replaceContent(data.content, { keepHistory: true });
      toast.success(data.placed.length ? `Foto inserite: ${data.placed.join(", ")}` : "Nessuna foto approvata da inserire");
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-5 px-3" data-testid="photo-slots">
      <p className="px-1 text-[11px] uppercase tracking-wider text-slate-500">Foto previste senza immagine ({st.pending.length + st.placed.length})</p>
      <ul className="mt-1 space-y-0.5 px-1" data-testid="photo-slots-list">
        {st.placed.map((name) => <li key={name} className="text-[11px] text-emerald-700 truncate" data-testid={`photo-slot-ready-${name}`}>{name} · foto approvata pronta</li>)}
        {st.pending.map((x) => (
          <li key={x.slot} className="text-[11px] text-slate-600 truncate" data-testid={`photo-slot-${x.slot}`}>
            {x.section} · {x.photo_ready ? (x.has_prop ? "foto approvata pronta" : "foto pronta, la sezione non ha lo spazio foto") : "grafica del brand"}
          </li>
        ))}
      </ul>
      {ready > 0 && <Button size="sm" className="mt-2 h-7 w-full text-[11px]" disabled={busy} onClick={insert} data-testid="photo-slots-insert">Inserisci foto approvate ({ready})</Button>}
    </div>
  );
}

function ReviewList({ api, projectId, ed }) {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/projects/${projectId}/images/review`).then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!items.length) return null;
  const approve = async (x) => {
    try {
      if (ed.saveState === "dirty") await ed.save();
      const { data } = await api.post(`/projects/${projectId}/images/${x.id}/approve`);
      if (data.content) ed.replaceContent(data.content, { keepHistory: true });
      toast.success(data.placed?.length ? `Immagine approvata e inserita in «${data.placed.join(", ")}»` : "Immagine approvata: ora puoi usarla da «Le tue immagini»");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="mt-5 px-3" data-testid="image-review-list">
      <p className="px-1 text-[11px] uppercase tracking-wider text-amber-700">Immagini AI da verificare ({items.length})</p>
      <p className="px-1 text-[11px] text-slate-500">Il controllo AI non è stato eseguito: non sono nel sito finché non le approvi.</p>
      <div className="mt-1.5 grid grid-cols-2 gap-2">
        {items.map((x, i) => (
          <div key={x.id} className="rounded-lg border border-amber-200 bg-white overflow-hidden" data-testid={`image-review-item-${i}`}>
            <img src={assetUrl(x.url)} alt="" className="aspect-[4/3] w-full object-cover" />
            <Button size="sm" variant="ghost" className="h-6 w-full text-[11px]" onClick={() => approve(x)} data-testid={`image-review-approve-${i}`}>Approva</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SiteImagesPanel() {
  const ed = useEditor();
  const { content, assets, setAssets, project, api, id } = ed;
  const images = useSiteImages(content, assets);
  const [target, setTarget] = useState(null);
  const credits = project.image_credits || [];
  const onPick = async (url, meta) => {
    if (meta?.source !== "ai_edit") {
      try { await api.post(`/projects/${id}/images/apply`, { from_url: target.url, to_url: url }); } catch (e) { toast.error(e?.response?.data?.detail || "Sostituzione non consentita"); return; }
    }
    const n = ed.replaceImage(target.url, url);
    toast.success(`Immagine sostituita in ${n} ${n === 1 ? "punto" : "punti"}`, { action: { label: "Annulla", onClick: ed.undo } });
  };
  return (
    <div className="pb-6" data-testid="site-images-panel">
      <PanelHeader title="Immagini del sito" subtitle={`${images.length} immagini usate`} />
      <div className="px-3 grid grid-cols-2 gap-2" data-testid="site-images-grid">
        {images.map((im, i) => (
          <div key={im.url} className="rounded-lg border bg-white overflow-hidden" data-testid={`site-image-${i}`}>
            <div className="relative aspect-[4/3] bg-slate-100">
              <img src={assetUrl(im.url)} alt="" loading="lazy" className={`h-full w-full ${im.origin === "logo" ? "object-contain p-2" : "object-cover"}`} />
              <span className="absolute top-1.5 left-1.5 rounded bg-white/90 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">{ORIGIN[im.origin]}</span>
            </div>
            <div className="flex items-center justify-between gap-1 pl-2 pr-1 py-1.5">
              <span className="text-[10px] text-slate-500 shrink-0">{im.count}×</span>
              <Button size="sm" variant="ghost" className="h-6 px-1 text-[11px] shrink-0" title="Sostituisci immagine" onClick={() => setTarget(im)} data-testid={`site-image-replace-${i}`}><RefreshCw size={11} className="mr-1" />Cambia</Button>
            </div>
          </div>
        ))}
      </div>
      {!images.length && <p className="px-4 text-xs text-slate-500">Nessuna immagine nel sito.</p>}
      {credits.length > 0 && (
        <div className="mt-5 px-4" data-testid="image-credits">
          <p className="text-[11px] uppercase tracking-wider text-slate-500">Crediti foto</p>
          <ul className="mt-1.5 space-y-1">{credits.map((c) => <li key={c.url} className="text-[11px] text-slate-600 truncate">Foto di <a href={c.author_url} target="_blank" rel="noreferrer" className="underline">{c.author}</a> su <a href={c.source_url} target="_blank" rel="noreferrer" className="underline capitalize">{c.provider}</a></li>)}</ul>
        </div>
      )}
      <PhotoSlots ed={ed} />
      <ReviewList api={api} projectId={id} ed={ed} />
      <ImagePickerDialog currentUrl={target?.url} open={!!target} onOpenChange={(o) => !o && setTarget(null)} api={api} projectId={id} assets={assets} setAssets={setAssets}
        initialQuery={project.sector || ""} onPick={onPick} title="Sostituisci immagine nel sito" usedUrls={images.map((x) => x.url)} noStock={!!project.no_stock} />
    </div>
  );
}
